import { useCallback, useEffect, useState } from 'react';
import {
  createDefaultFundingChecklist,
  createFundingDocument,
  deleteFundingDocument,
  fetchClientFundingDocuments,
  fetchFundingDocuments,
  updateFundingDocument,
} from '../../services/funding/fundingDocumentsService';

export function useFundingDocuments({
  agencyId,
  fundingClientId,
  status,
  category,
  autoLoad = true,
} = {}) {
  const [documents, setDocuments] = useState([]);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (fundingClientId && agencyId) {
        const result = await fetchClientFundingDocuments(fundingClientId, agencyId);
        setDocuments(result.documents);
        setDocumentStatus(result.documentStatus);
        return result;
      }

      const result = await fetchFundingDocuments({ agencyId, fundingClientId, status, category });
      setDocuments(result.documents);
      setDocumentStatus(null);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load funding documents');
      setDocuments([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [agencyId, fundingClientId, status, category]);

  const requestDocument = useCallback(async (clientId, payload) => {
    setSaving(true);
    setError(null);
    try {
      const doc = await createFundingDocument(clientId, agencyId, payload);
      await load();
      return doc;
    } catch (err) {
      setError(err?.message || 'Failed to request document');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const createChecklist = useCallback(async (clientId) => {
    setSaving(true);
    setError(null);
    try {
      const result = await createDefaultFundingChecklist(clientId, agencyId);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create default checklist');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const updateDocument = useCallback(async (documentId, payload) => {
    setSaving(true);
    setError(null);
    try {
      const doc = await updateFundingDocument(documentId, agencyId, payload);
      await load();
      return doc;
    } catch (err) {
      setError(err?.message || 'Failed to update document');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const removeDocument = useCallback(async (documentId) => {
    setSaving(true);
    setError(null);
    try {
      await deleteFundingDocument(documentId, agencyId);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to delete document');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  useEffect(() => {
    if (autoLoad) {
      load();
    }
  }, [autoLoad, load]);

  return {
    documents,
    documentStatus,
    loading,
    saving,
    error,
    load,
    requestDocument,
    createChecklist,
    updateDocument,
    removeDocument,
  };
}
